import React, { useState, useEffect } from "react";
import { Box, Tabs, Tab } from "@mui/material";
import { ConfirmationDialog } from "@1823-partners/core";
import FixedIncomeRecon from "./fixedIncome/FixedIncomeRecon";
import ExcludedPositions from "./ExcludedPositions";
import Crosswalk from "./Crosswalk";
import DerivativeReconDashboard from "./derivative/DerivativeReconDashboard";
import RemittanceDashboard from "./remittance/RemittanceDashboard";
import { readRemittanceDeepLink } from "../utils/remittanceDeepLink";

const Dashboard = ({ userId }) => {
  const [remittanceDeepLink] = useState(() => readRemittanceDeepLink(window.location.search));
  const [excludedPositionsRefreshKey, setExcludedPositionsRefreshKey] = useState(0);
  const [selectedTab, setSelectedTab] = useState(remittanceDeepLink.active ? 4 : 0);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const handleSelectRecon = (rowData) => {
    // Row selected for history view
  };

  const handleSaveSuccess = () => {
    // Save success handler - can be extended if needed
  };

  const handleExclusionSuccess = () => {
    // Refresh the excluded positions grid
    setExcludedPositionsRefreshKey(prev => prev + 1);
  };

  // Leaving the Fixed Income tab unmounts the recon grid and its unsaved
  // comments, so ask first.
  const [reconDirty, setReconDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);

  const handleTabChange = (event, newValue) => {
    if (selectedTab === 0 && newValue !== 0 && reconDirty) {
      setPendingTab(newValue);
      return;
    }
    setSelectedTab(newValue);
  };

  const discardAndSwitchTab = () => {
    setSelectedTab(pendingTab);
    setPendingTab(null);
    setReconDirty(false);
  };

  // Update viewport height on window resize
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={selectedTab} onChange={handleTabChange} aria-label="Recon dashboard navigation">
          <Tab label="Fixed Income Recon" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="Excluded Positions" id="tab-1" aria-controls="tabpanel-1" />
          <Tab label="Crosswalk" id="tab-2" aria-controls="tabpanel-2" />
          <Tab label="Derivative Recon" id="tab-3" aria-controls="tabpanel-3" />
          <Tab label="CML Remittance" id="tab-4" aria-controls="tabpanel-4" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box
          role="tabpanel"
          id="tabpanel-0"
          aria-labelledby="tab-0"
          sx={{
            flex: 1,
            overflow: "hidden",
            px: 2,
            pb: 2,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <FixedIncomeRecon
              onSelectEntry={handleSelectRecon}
              onSaveSuccess={handleSaveSuccess}
              onExclusionSuccess={handleExclusionSuccess}
              onUnsavedChange={setReconDirty}
              userId={userId}
              height={viewportHeight - 150}
            />
          </Box>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box
          role="tabpanel"
          id="tabpanel-1"
          aria-labelledby="tab-1"
          sx={{
            flex: 1,
            overflow: "hidden",
            px: 2,
            pb: 2,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ExcludedPositions
              userId={userId}
              height={600}
              refreshKey={excludedPositionsRefreshKey}
            />
          </Box>
        </Box>
      )}

      {selectedTab === 2 && (
        <Box
          role="tabpanel"
          id="tabpanel-2"
          aria-labelledby="tab-2"
          sx={{
            flex: 1,
            overflow: "hidden",
            px: 2,
            pb: 2,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Crosswalk
              userId={userId}
              height={600}
            />
          </Box>
        </Box>
      )}

      {selectedTab === 3 && (
        <Box
          role="tabpanel"
          id="tabpanel-3"
          aria-labelledby="tab-3"
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            pb: 2,
            pt: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <DerivativeReconDashboard />
        </Box>
      )}

      {selectedTab === 4 && (
        <Box
          role="tabpanel"
          id="tabpanel-4"
          aria-labelledby="tab-4"
          sx={{
            flex: 1,
            overflow: "auto",
            px: 2,
            pb: 2,
            pt: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0
          }}
        >
          <RemittanceDashboard
            height={viewportHeight - 150}
            initialRunId={remittanceDeepLink.runId}
          />
        </Box>
      )}

      <ConfirmationDialog
        open={pendingTab !== null}
        onClose={() => setPendingTab(null)}
        onConfirm={discardAndSwitchTab}
        title="Discard unsaved comments?"
        confirmText="Discard and switch"
        confirmColor="error"
        message="The month-end recon is holding comments that have not been saved. Switching tabs discards them."
      />
    </Box>
  );
};

export default Dashboard;
